// One Global Search session = overlay opens → user closes it or opens a result.
// The whole session is sent as a single `global_search_session` log event so we
// can see where users struggle (refinement chains, empty results, give-ups).
// Pure bookkeeping — no React — so it can be unit-tested directly.
import type { OrderedGroup } from './hooks/useGlobalSearch'
import { PageResult } from './pageDirectory'
import { GlobalSearchErrorCode, resolveDeeplink, SearchTypeKey } from './schema'

export type OpenVia = 'shortcut' | 'trigger'
export type CloseReason = 'escape' | 'backdrop' | 'shortcut' | 'unmount' | 'pagehide'
export type QueryVia = 'typed' | 'recent' | 'suggestion'
export type ActivationVia = 'click' | 'enter'

// Per-type totals from the backend: how much more exists than was shown.
export interface StepGroup {
    type: SearchTypeKey
    total: number
    shown: number
    partial: boolean
}

// One row as the user saw it. `type` is the record type, or 'view' for a
// Control Panel page / filtered view (same convention as RecentResult).
// The label is what the row said; for orders and memberships that includes
// customer names and emails — see serializeSensitiveText.
export interface StepResult {
    type: SearchTypeKey | 'view'
    label: string
    link: string
    id?: string
}

// One settled query: the user paused typing and the search request finished.
export interface SessionStep {
    query: string
    via: QueryVia
    // Overlay opened → this query's results first on screen.
    open_to_results_ms: number
    // Time spent waiting on the search backend for this query, summed over
    // retries and the email fallback. Excludes typing and the debounce window.
    loading_ms: number
    groups: StepGroup[]
    // Every row on screen, in display order: pages first, then records. The
    // array index is the `position` an outcome refers to.
    results: StepResult[]
    scoped_view: boolean
    reservation_fallback: boolean
    email_fallback_used: boolean
    filters_dropped: boolean
    error: GlobalSearchErrorCode | null
    empty: boolean
    retries: number
    // > 1 means the same query settled again (retry, email fallback, refetch).
    settled_count: number
}

export type SettledStepInput = Omit<SessionStep, 'via' | 'open_to_results_ms' | 'retries' | 'settled_count'>

export type SessionOutcome =
    | { kind: 'result'; type: SearchTypeKey; id: string; position: number; via: ActivationVia }
    | { kind: 'page'; path: string; position: number; scoped: boolean; via: ActivationVia }
    | { kind: 'view_all'; type: SearchTypeKey }
    | { kind: 'recent_result'; type: SearchTypeKey | 'view'; position: number; via: ActivationVia }
    | { kind: 'abandoned'; close_reason: CloseReason }

// Mutable; lives in a ref for the lifetime of one overlay open.
export interface SearchSession {
    id: string
    startedAt: number
    openedVia: OpenVia
    steps: SessionStep[]
    // Consumed by the next appended step, then reset to 'typed'.
    pendingVia: QueryVia
    // Set by "Try again", consumed when the same query settles again.
    pendingRetry: boolean
    cleared: number
    inputTouched: boolean
    outcome: SessionOutcome | null
    sent: boolean
}

export interface SessionPayload {
    session_id: string
    opened_via: OpenVia
    // Overlay opened → result opened or given up.
    open_to_outcome_ms: number
    query_count: number
    queries: string[]
    steps: SessionStep[]
    steps_truncated: boolean
    had_error: boolean
    had_empty: boolean
    retry_count: number
    cleared_count: number
    input_touched: boolean
    outcome: SessionOutcome
    query_at_outcome: string
}

export const MAX_STEPS = 30

// Single choke point for every free-text value that leaves the browser:
// queries and result labels. Identity today; the place to plug in encryption
// later (both carry customer emails and order numbers, and the backend must
// still be able to read them).
export const serializeSensitiveText = (text: string): string => text

export const createSession = (id: string, openedVia: OpenVia, now: number): SearchSession => ({
    id,
    startedAt: now,
    openedVia,
    steps: [],
    pendingVia: 'typed',
    pendingRetry: false,
    cleared: 0,
    inputTouched: false,
    outcome: null,
    sent: false,
})

export const groupsToStepGroups = (orderedGroups: OrderedGroup[]): StepGroup[] =>
    orderedGroups.map(({ meta, group }) => ({
        type: meta.key,
        total: group.total,
        shown: group.results.length,
        partial: group.partial,
    }))

// The rows in the order the overlay renders them (and the keyboard walks them).
export const stepResultsFrom = (
    pageResults: PageResult[],
    orderedGroups: OrderedGroup[],
    accountSlug: string | null,
): StepResult[] => [
    ...pageResults.map((page) => ({ type: 'view' as const, label: page.title, link: page.path })),
    ...orderedGroups.flatMap(({ group }) =>
        group.results.map((result) => ({
            type: result.type,
            label: result.title,
            link: accountSlug ? resolveDeeplink(result, accountSlug) : result.deeplink,
            id: result.id,
        })),
    ),
]

// A query that settles again right after itself (retry, email fallback, a
// refetch) updates the last step instead of adding a new one, so the steps
// list reads as the user's refinement chain.
export const appendSettledStep = (session: SearchSession, input: SettledStepInput, now: number): void => {
    const last = session.steps[session.steps.length - 1]
    if (last && last.query === input.query) {
        const loadingMs = last.loading_ms + input.loading_ms
        Object.assign(last, input)
        last.loading_ms = loadingMs
        last.settled_count += 1
        if (session.pendingRetry) {
            last.retries += 1
            session.pendingRetry = false
        }
        return
    }
    session.pendingRetry = false
    session.steps.push({
        ...input,
        via: session.pendingVia,
        open_to_results_ms: now - session.startedAt,
        retries: 0,
        settled_count: 1,
    })
    session.pendingVia = 'typed'
}

export const markNextQueryVia = (session: SearchSession, via: QueryVia): void => {
    session.pendingVia = via
}

export const markRetry = (session: SearchSession): void => {
    session.pendingRetry = true
}

export const markCleared = (session: SearchSession): void => {
    session.cleared += 1
}

export const markInputTouched = (session: SearchSession): void => {
    session.inputTouched = true
}

// First write wins: a result click records its outcome before the overlay's
// close callback fires, and the close must not overwrite it with "abandoned".
export const setOutcome = (session: SearchSession, outcome: SessionOutcome): void => {
    if (!session.outcome) {
        session.outcome = outcome
    }
}

export const buildSessionPayload = (session: SearchSession, queryAtOutcome: string, now: number): SessionPayload => {
    const steps = session.steps.slice(0, MAX_STEPS).map((step) => ({
        ...step,
        query: serializeSensitiveText(step.query),
        results: step.results.map((result) => ({ ...result, label: serializeSensitiveText(result.label) })),
    }))
    return {
        session_id: session.id,
        opened_via: session.openedVia,
        open_to_outcome_ms: Math.max(0, now - session.startedAt),
        query_count: session.steps.length,
        queries: steps.map((step) => step.query),
        steps,
        steps_truncated: session.steps.length > MAX_STEPS,
        had_error: session.steps.some((step) => step.error !== null),
        had_empty: session.steps.some((step) => step.empty),
        retry_count: session.steps.reduce((sum, step) => sum + step.retries, 0),
        cleared_count: session.cleared,
        input_touched: session.inputTouched,
        outcome: session.outcome ?? { kind: 'abandoned', close_reason: 'unmount' },
        query_at_outcome: serializeSensitiveText(queryAtOutcome.trim()),
    }
}
